import clsx from 'clsx'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import { ComputeFactory } from '../../models/ComputeFactory'
import { Dashboard } from '../../models/Dashboard'
import { Item } from '../../models/Item'
import { RecordDataMap } from '../../models/RecordDataMap'
import { RecordFactory } from '../../models/RecordFactory'
import { subscriptionPlanOptions } from '../../models/Subscription'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import {
    PREMIUM_FRANCHISE_ID,
    PREMIUM_ID,
    PREMIUM_PLUS_ID,
    PREMIUM_SUITE_ID,
} from '../../utils/constants'
import { currencyFormat } from '../../utils/numbers'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import { PSApplication } from '../app/PSApplication'
import { PlanCardView } from '../app/PlanCardView'
import Button from '../app/design/Button'
import Modal from '../app/design/Modal'
import Paragraph from '../app/design/Paragraph'
import { ListHeader } from '../common/ListHeader'
import { ConfirmationModal } from '../common/modals/ConfirmationModal'
import { GuardrailModal } from '../common/modals/GuardrailModal'
import { CardPage } from '../payments/CardPage'
import { ConfirmBasicPlanModal } from '../onboarding/ConfirmBasicPlanModal'
import { SetupBillingPage } from './SetupBillingPage'

const ACTION_BUTTON = 1
const ACTION_EXTRABUTTON = 2
type GuardrailMetadata = {
    price?: string
    currentTier: string
    buttonText: string
    extraButtonText?: string
    handleClick: (buttonIx?: typeof ACTION_BUTTON | typeof ACTION_EXTRABUTTON) => void
}

export type HandleUpgradeOpts = {
    cancel?: boolean
}

type Props = {
    showHeader?: boolean
    context: 'change' | 'new' | 'team' | 'comeback' | 'feature' | 'affiliate'
    premiumItems?: Item[] // For ABMP Basic confirmation
    planId?: string
    onNext?: () => void
    onSkip?: () => void
    handleUpgrade?: (opts?: HandleUpgradeOpts) => void
    registerOnBack?: (onBack: () => void) => void
    isInWorkflow?: boolean
    onBack?: {
        label: string
        href?: string
    }
}

const viewWillAppear = async ({ props: { context }, me: user }: PocketComponent<Props>) => {
    const plansResult = await RecordFactory.getInstance().fetchPlans(user, context)
    if (!plansResult.ok) {
        return new RecordNotFoundError('Plans not found')
    }

    const plans: RecordDataMap[] = plansResult.value.plans

    const editionID =
        plans.find((plan) => plan.plan === 'premium' && plan.edition)?.edition?.id ??
        user.edition?.id
    const edition = !!editionID
        ? await RecordFactory.getInstance().loadEdition(editionID)
        : undefined

    const allowFreeTrial = await ComputeFactory.getInstance().allowFreeTrial()

    const [subscription, sources] = await Promise.all([
        ComputeFactory.getInstance().fetchPremiumSubscription(),
        RecordFactory.getInstance().loadSources(),
    ])

    return {
        plans,
        edition,
        response: plansResult.value,
        allowFreeTrial,
        billing: {
            plans: subscriptionPlanOptions(subscription, edition),
            sources,
            subscription,
        },
    }
}

const render = ({
    base: {
        props: {
            context,
            premiumItems,
            planId,
            showHeader = true,
            onNext,
            onSkip,
            handleUpgrade,
            registerOnBack,
            onBack,
            isInWorkflow,
        },
        me: user,
        showProgressBar,
        hideProgressBar,
        logAndDisplayError,
    },
    data,
}: PocketComponentProps<ComponentData<typeof viewWillAppear>, Props>) => {
    const navigate = usePSNavigate()

    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const { plans, edition, response, allowFreeTrial, billing } = data

    const [planGuardrail, setPlanGuardRail] = useState<GuardrailMetadata>()
    const [showUpgradeGuardrail, setShowUpgradeGuardrail] = useState(false)
    const [showDowngradeGuardrail, setShowDowngradeGuardrail] = useState(false)
    const [showCancelGuardrail, setShowCancelGuardrail] = useState(false)
    const [showDoubleDownGuardrail, setShowDoubleDownGuardrail] = useState(false)
    const [showContactUsGuardrail, setShowContactUsGuardrail] = useState(false)

    const [showSuccessfulDoubleDown, setShowSuccessfulDoubleDown] = useState(false)
    const [showSuccessfulTrialUpgrade, setShowSuccessfulTrialUpgrade] = useState(false)
    const [planTitle, setPlanTitle] = useState('')
    const [showBasicConfirmation, setShowBasicConfirmation] = useState(false)

    const calculateTier = (tierName: string | undefined) => {
        switch (tierName) {
            case 'premium':
                return 2
            case 'plus':
                return 3
            case 'suite':
                return 4
            default:
                return 1
        }
    }

    const { title, description, subtitle } = useMemo(() => {
        if (!response) return { title: '', description: '', subtitle: '' }
        if (response.welcome_title)
            return {
                title: response.welcome_title,
                description: response.welcome,
                subtitle: response.welcome_subtitle,
            }
        return { title: response.title, description: '', subtitle: '' }
    }, [response])

    const tappedContactUs = useCallback(
        (plan: RecordDataMap) => {
            setSelectedPlan(plan)
            setShowContactUsGuardrail(true)
        },
        [user.id, edition?.id]
    )

    const tappedDoubleDown = useCallback(async (plan: RecordDataMap) => {
        const alertsAffiliateDashboard = new Dashboard({ id: 'alerts_affiliate' })

        showProgressBar()

        setSelectedPlan(plan)
        const result = await RecordFactory.getInstance().markDashboard(
            alertsAffiliateDashboard,
            'buy_annual',
            undefined
        )
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        setShowSuccessfulDoubleDown(true)
        hideProgressBar()
    }, [])

    const tappedTestDrive = useCallback(
        async (plan: RecordDataMap) => {
            showProgressBar()
            setPlanTitle(plan.title ?? '')
            const result = await RecordFactory.getInstance().subscribe(
                user,
                plan.plan,
                'premium' === plan.plan
                    ? PREMIUM_ID
                    : 'plus' === plan.plan
                      ? PREMIUM_PLUS_ID
                      : 'franchise' === plan.plan
                        ? PREMIUM_FRANCHISE_ID
                        : PREMIUM_SUITE_ID,
                undefined,
                30,
                undefined,
                undefined,
                undefined,
                edition
            )
            setShowSuccessfulTrialUpgrade(true)

            hideProgressBar()

            if (!result.ok) {
                logAndDisplayError(result.error)
            }
        },
        [user.id, edition?.id]
    )

    const [selectedPlan, setSelectedPlan] = useState<RecordDataMap>()
    const [showBilling, setShowBilling] = useState(false)
    const [showCardEdit, setShowCardEdit] = useState(false)
    const tappedSubscribe = useCallback(
        (plan: RecordDataMap) => {
            if (!user.id) return

            setPlanGuardRail(undefined)

            setSelectedPlan(plan)
            setShowBilling(true)
        },
        [user?.id]
    )

    const handleBack = useCallback(() => {
        if (!showCardEdit && !showBilling) {
            navigate(-1)
            return
        }

        setShowBilling(showCardEdit)
        setShowCardEdit(false)
    }, [showCardEdit, showBilling])

    useEffect(() => {
        registerOnBack?.(handleBack)
    }, [handleBack, registerOnBack])

    const tappedUnsubscribe = useCallback(() => {
        navigate('/settings/change-plan/cancel')
    }, [])

    const getNextAction = useCallback(
        (
            action:
                | 'subscribe'
                | 'unsubscribe'
                | 'testdrive'
                | 'doubledown'
                | 'contactus'
                | 'affiliate'
        ) => {
            return {
                contactus: tappedContactUs,
                subscribe: tappedSubscribe,
                testdrive: tappedTestDrive,
                doubledown: tappedDoubleDown,
                unsubscribe: tappedUnsubscribe,
                affiliate: tappedDoubleDown,
            }[action]
        },
        [tappedSubscribe, tappedTestDrive, tappedUnsubscribe, tappedDoubleDown]
    )

    const planSelected = useCallback(
        (plan: RecordDataMap) => {
            const planId = plan.plan

            if (planId === 'free' && ['new', 'comeback'].includes(context)) {
                const user = {
                    ...PSApplication.me(),
                    requires_upgrade: false,
                }
                RecordFactory.getInstance().storeUser(user)

                if (premiumItems && premiumItems.length > 0) {
                    setShowBasicConfirmation(true)
                }
                else if (onNext) {
                    return onNext()
                }
            }

            setPlanGuardRail({
                buttonText:
                    (plan.subscribe_button_actions?.length ?? 0) > 0
                        ? (plan.subscribe_button_actions![0].alert ?? 'Upgrade')
                        : 'Upgrade',
                extraButtonText:
                    (plan.subscribe_button_actions?.length ?? 0) > 1
                        ? plan.subscribe_button_actions![1].alert
                        : undefined,
                price: plan.price,
                currentTier: plan.title ?? '',
                handleClick: (buttonIx?: typeof ACTION_BUTTON | typeof ACTION_EXTRABUTTON) => {
                    if (plan.subscribe_button_actions?.length) {
                        let action = undefined
                        if (buttonIx === ACTION_BUTTON) {
                            action = getNextAction(plan.subscribe_button_actions![0].id)
                        }
                        else if (
                            buttonIx === ACTION_EXTRABUTTON &&
                            (plan.subscribe_button_actions?.length ?? 0) >= 2
                        ) {
                            action = getNextAction(plan.subscribe_button_actions![1].id)
                        }
                        if (action) action(plan)
                    }
                },
            })
            const currentTier = calculateTier(user.plan)
            const requestedTier = calculateTier(planId)
            const forContactUs =
                (plan.subscribe_button_actions?.length ?? 0) == 1 &&
                plan.subscribe_button_actions![0].id == 'contactus'

            if (context === 'affiliate') setShowDoubleDownGuardrail(true)
            else if (requestedTier === currentTier && forContactUs) setShowContactUsGuardrail(true)
            else if (requestedTier === currentTier && planId !== 'free')
                setShowCancelGuardrail(true)
            else if (requestedTier < currentTier) setShowDowngradeGuardrail(true)
            else if (requestedTier > currentTier) setShowUpgradeGuardrail(true)
            else if (onNext) onNext()
        },
        [user, context, premiumItems, onNext, getNextAction]
    )

    const closeSuccessModal = useCallback(() => {
        setShowSuccessfulTrialUpgrade(false)

        if (onNext) onNext()
    }, [onNext])

    useEffect(() => {
        if (context !== 'feature' || !response || !planId || planGuardrail) return

        const plan = plans.find((p) => p.plan === planId)
        if (plan) planSelected(plan)
    }, [context, user, response, planId, planGuardrail, plans, planSelected])

    const onUpgradeGuardrailClose = () => {
        setShowUpgradeGuardrail(false)
        handleUpgrade?.({ cancel: true })
    }

    const onUpgradeGuardrailAction = (
        guardRail: GuardrailMetadata,
        buttonIx: typeof ACTION_BUTTON | typeof ACTION_EXTRABUTTON
    ) => {
        guardRail.handleClick(buttonIx)
        handleUpgrade?.()
    }

    if (showBilling && selectedPlan) {
        return showCardEdit ? (
            <CardPage onBack={() => setShowCardEdit(false)} />
        ) : (
            <SetupBillingPage
                plan={selectedPlan}
                onNext={onNext}
                plans={billing.plans}
                sources={billing.sources}
                subscription={billing.subscription}
                onBack={() => {
                    if (!showCardEdit && Boolean(handleUpgrade)) {
                        handleUpgrade?.({ cancel: true })
                        return
                    }
                    setSelectedPlan(undefined)
                }}
            />
        )
    }

    return (
        <div
            className={clsx('relative h-screen w-full py-6', {
                'overflow-y-auto': !isInWorkflow,
            })}
        >
            <div className="max-w-large mx-auto">
                {context !== 'feature' && (
                    <div>
                        {showHeader && (
                            <ListHeader
                                title={title}
                                backButton={
                                    onBack
                                        ? onBack
                                        : {
                                              label: 'Settings',
                                              href: '/settings',
                                          }
                                }
                            />
                        )}

                        <div className="mt-2 flex flex-col gap-y-2">
                            {description && (
                                <Paragraph className="ml-8 text-gray-600">{description}</Paragraph>
                            )}

                            {subtitle && (
                                <Paragraph className="ml-8 text-gray-600">{subtitle}</Paragraph>
                            )}
                        </div>

                        <div className="mt-4 flex flex-col gap-y-4">
                            {plans.map((plan) => (
                                <PlanCardView
                                    key={plan.plan}
                                    allowFreeTrial={allowFreeTrial}
                                    plan={plan}
                                    onPlanSelected={planSelected}
                                    edition={edition}
                                />
                            ))}
                        </div>
                    </div>
                )}

                <Modal
                    isOpen={Boolean(planGuardrail && showUpgradeGuardrail)}
                    onClose={onUpgradeGuardrailClose}
                    title={planGuardrail?.currentTier}
                    footer={
                        <div className="flex justify-end gap-x-2">
                            {planGuardrail?.extraButtonText && (
                                <Button
                                    intent="secondary"
                                    onClick={() =>
                                        onUpgradeGuardrailAction(planGuardrail, ACTION_EXTRABUTTON)
                                    }
                                >
                                    {planGuardrail.extraButtonText}
                                </Button>
                            )}
                            <Button
                                intent="primary"
                                onClick={() =>
                                    onUpgradeGuardrailAction(planGuardrail!, ACTION_BUTTON)
                                }
                            >
                                {planGuardrail?.buttonText}
                            </Button>
                        </div>
                    }
                >
                    <div>
                        {planGuardrail?.extraButtonText ? (
                            <Paragraph>
                                Select whether you'd like to start a 30-day free trial or upgrade to{' '}
                                {planGuardrail.currentTier}
                                {planGuardrail.price ? ` for $${planGuardrail.price}/month.` : '.'}
                            </Paragraph>
                        ) : (
                            <Paragraph>
                                Would you like to upgrade your PocketSuite subscription?
                            </Paragraph>
                        )}
                    </div>
                </Modal>

                {planGuardrail && showDowngradeGuardrail && (
                    <GuardrailModal
                        disableDismiss
                        title="Downgrade"
                        buttonText={planGuardrail.buttonText}
                        onClose={() => setShowDowngradeGuardrail(false)}
                        onAction={() => planGuardrail.handleClick(ACTION_BUTTON)}
                    >
                        <Paragraph>
                            Would you like to downgrade to PocketSuite {planGuardrail.currentTier}?
                        </Paragraph>
                    </GuardrailModal>
                )}
                {planGuardrail && showCancelGuardrail && (
                    <GuardrailModal
                        disableDismiss
                        title="Cancel"
                        buttonText={planGuardrail.buttonText}
                        onClose={() => setShowCancelGuardrail(false)}
                        onAction={() => planGuardrail.handleClick(ACTION_BUTTON)}
                    >
                        <Paragraph>
                            Would you like to cancel your PocketSuite subscription?
                        </Paragraph>
                    </GuardrailModal>
                )}
                {planGuardrail && showDoubleDownGuardrail && (
                    <GuardrailModal
                        disableDismiss
                        title="Upgrade"
                        buttonText={planGuardrail.buttonText}
                        onClose={() => setShowDoubleDownGuardrail(false)}
                        onAction={() => {
                            planGuardrail.handleClick(ACTION_BUTTON)
                        }}
                    >
                        <Paragraph>
                            Would you like to upgrade to the annual{' '}
                            {planGuardrail.currentTier.toLowerCase()} plan and save $50?
                        </Paragraph>
                    </GuardrailModal>
                )}
                {planGuardrail && showContactUsGuardrail && (
                    <GuardrailModal
                        disableDismiss
                        title="Contact us"
                        buttonText="Dismiss"
                        onClose={() => setShowContactUsGuardrail(false)}
                        onAction={() => {
                            setPlanGuardRail(undefined)
                            setShowContactUsGuardrail(false)
                        }}
                    >
                        <Paragraph>{planGuardrail.buttonText}</Paragraph>
                    </GuardrailModal>
                )}

                <ConfirmationModal
                    isOpen={showSuccessfulTrialUpgrade}
                    onClose={closeSuccessModal}
                    onConfirm={closeSuccessModal}
                    shouldHideCancelButton
                    confirmButtonText="Dismiss"
                    title="Success!"
                >
                    <Paragraph>
                        You've just upgraded to a 30 day free trial of PocketSuite {planTitle}!
                    </Paragraph>
                </ConfirmationModal>

                <ConfirmationModal
                    isOpen={Boolean(showSuccessfulDoubleDown && selectedPlan)}
                    onClose={() => {
                        setPlanGuardRail(undefined)
                        setShowSuccessfulDoubleDown(false)
                        onNext?.()
                    }}
                    onConfirm={() => {
                        setPlanGuardRail(undefined)
                        setShowSuccessfulDoubleDown(false)
                        onNext?.()
                    }}
                    shouldHideCancelButton
                    confirmButtonText="Dismiss"
                    title="Congratulations!"
                >
                    <Paragraph>
                        You have successfully authorized an upgrade of your account to our{' '}
                        {selectedPlan?.title} Annual Plan for{' '}
                        {currencyFormat(Number(selectedPlan?.yearly) - Number(50))}.
                    </Paragraph>
                </ConfirmationModal>

                {showBasicConfirmation && premiumItems && premiumItems.length > 0 && (
                    <ConfirmBasicPlanModal
                        premiumItems={premiumItems}
                        onCancel={() => setShowBasicConfirmation(false)}
                        onBasic={() => {
                            setShowBasicConfirmation(false)
                            onNext?.()
                        }}
                        onPremium={() => {
                            setShowBasicConfirmation(false)
                            const plan = plans.filter((plan) => plan.plan === 'premium')
                            if (plan.length > 0) {
                                tappedTestDrive(plan[0])
                            }
                        }}
                    />
                )}
            </div>
        </div>
    )
}

export const SelectPlanPage = usePSComponent(render, viewWillAppear)
